import React, { useState, useEffect } from 'react';
import { createSupportMessage, getUserSupportMessages } from '../services/supportService';
import { auth } from '../services/firebase';
import { formatDistanceToNow } from 'date-fns';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [userMessages, setUserMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    // Fetch user's previous messages if logged in
    const fetchUserMessages = async () => {
      if (auth.currentUser) {
        setLoading(true);
        try {
          const messages = await getUserSupportMessages();
          setUserMessages(messages);
        } catch (err) {
          console.error('Error fetching user messages:', err);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchUserMessages();
  }, []);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Check if user is logged in
      if (!auth.currentUser) {
        throw new Error('You must be logged in to send a support message');
      }
      
      // Send message to Firebase
      await createSupportMessage({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message
      });
      
      // Clear form and show success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setSubmitSuccess(true);
      
      // Refresh user messages
      const messages = await getUserSupportMessages();
      setUserMessages(messages);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitSuccess(false);
      }, 5000);
    } catch (err) {
      console.error('Error submitting support message:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // FAQ items
  const faqItems = [
    {
      question: "How do I make a prediction?",
      answer: "To make a prediction, navigate to the Matches page, select a match, and click on the prediction type you want to make. Follow the on-screen instructions to complete your prediction."
    },
    {
      question: "How do I earn coins?",
      answer: "You can earn coins by winning predictions, completing daily tasks, referring friends, or participating in special events. Check the Earn page for more details."
    },
    {
      question: "What happens if a match is cancelled or postponed?",
      answer: "If a match is cancelled or postponed, all predictions for that match will be voided and any coins used will be refunded to your wallet."
    },
    {
      question: "Can I withdraw my prediction?",
      answer: "Predictions cannot be withdrawn once submitted. Make sure to review your prediction before confirming."
    },
    {
      question: "How are winnings calculated?",
      answer: "Winnings are calculated by multiplying your prediction amount by the multiplier for that prediction type. Different prediction types have different multipliers."
    }
  ];
  
  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-6 text-gray-900 dark:text-white">Support Center</h1>
      
      {/* Contact Form */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Contact Us</h2>
        
        {submitSuccess ? (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            <p>Thank you for your message! Our team will get back to you soon.</p>
          </div>
        ) : null}
        
        {error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        ) : null}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Your Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="5"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              required
            ></textarea>
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || !auth.currentUser}
            className={`w-full py-2 px-4 rounded-md text-white font-medium ${
              isSubmitting || !auth.currentUser
                ? 'bg-blue-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </button>
          
          {!auth.currentUser && (
            <p className="mt-2 text-sm text-red-600">
              You must be logged in to send a support message.
            </p>
          )}
        </form>
      </div>
      
      {/* User's Previous Messages */}
      {auth.currentUser && userMessages.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Your Messages</h2>
          
          {loading ? (
            <p className="text-gray-600 dark:text-gray-400">Loading your messages...</p>
          ) : (
            <div className="space-y-4">
              {userMessages.map((msg) => (
                <div key={msg.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">{msg.subject}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      msg.status === 'replied' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                    }`}>
                      {msg.status === 'replied' ? 'Replied' : 'Pending'}
                    </span>
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 mb-2">{msg.message}</p>
                  
                  <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                    Sent {formatDistanceToNow(msg.createdAt)} ago
                  </div>
                  
                  {msg.replies && msg.replies.length > 0 && (
                    <div className="mt-4 pl-4 border-l-2 border-blue-500">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white mb-2">Admin Response:</h4>
                      {msg.replies.map((reply, index) => (
                        <div key={index} className="mb-2">
                          <p className="text-gray-700 dark:text-gray-300">{reply.text}</p>
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {reply.createdAt ? `${formatDistanceToNow(reply.createdAt.toDate())} ago` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* FAQs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">Frequently Asked Questions</h2>
        
        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <div key={index} className="border-b border-gray-200 dark:border-gray-700 pb-4">
              <h3 className="text-lg font-medium mb-2 text-gray-900 dark:text-white">{item.question}</h3>
              <p className="text-gray-700 dark:text-gray-300">{item.answer}</p>
            </div>
          ))}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-gray-700 dark:text-gray-300">
            Can't find the answer you're looking for? Contact our support team using the form above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Support; 